/**
 * settings/cards/ReportingCard.tsx
 * --------------------------------
 * What: Triggers generation of item reports and links to downloads.
 * Why: Groups reporting actions where users expect them.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Modal, ActivityIndicator } from 'react-native';
import ThemedButton from '../../../components/ThemedButton';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../../lib/ThemeContext';
import { useAuth } from '../../../../lib/AuthContext';
import { useSubscription } from '../../../../lib/SubscriptionContext';
import { supabase } from '../../../../lib/supabase';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function ReportingCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();
  const { user } = useAuth();
  const { currency, dateFormat } = useSubscription();

  // Modal & generation state
  const [showModal, setShowModal] = useState(false);
  const [reportType, setReportType] = useState<'all' | 'selected'>('all');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleGenerate = useCallback(async () => {
    if (!user?.email) {
      setMessage('User not loaded.');
      return;
    }
    setLoading(true);
    setMessage('Starting report generation');
    try {
      const { error } = await supabase.functions.invoke('generate-item-report', {
        body: {
          email: user.email,
          reportType,
          userId: user.id,
          currency,
          dateFormat,
        },
      });
      if (error) throw error;
      setLoading(false);
      setMessage('Report generation started. You will receive an email shortly.');
    } catch {
      setLoading(false);
      setMessage('Failed to generate report. Please try again.');
    }
  }, [user, reportType, currency, dateFormat]);

  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? (Platform.OS === 'web'
          ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
          : {
              shadowColor: theme.accent,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 6,
              elevation: 6,
            })
      : {}
  ), [themeType, theme.accent]);

  const cardStyle = [
    localStyles.cardBase,
    { backgroundColor: theme.card, borderColor: theme.border },
    glowStyle
  ];

  return (
    <View style={cardStyle}>
      <TouchableOpacity style={[localStyles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[localStyles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Reporting</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>
      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />
          <Text style={[localStyles.detailsValue, { color: theme.text }]}>
            Generate a detailed PDF report of your items. Useful for insurance claims, moving, or inventory management.
          </Text>
          <TouchableOpacity
            style={[localStyles.actionButton, { backgroundColor: theme.accent }]}
            onPress={() => setShowModal(true)}
          >
            <Ionicons name="document-text-outline" size={20} color={theme.buttonText} />
            <Text style={[localStyles.actionButtonText, { color: theme.buttonText }]}>Generate Item Report</Text>
          </TouchableOpacity>
          {/* Report generation modal */}
          <Modal
            visible={showModal}
            transparent
            animationType="fade"
            onRequestClose={() => {
              if (!loading) {
                setShowModal(false);
                setMessage('');
              }
            }}
          >
            <View style={localStyles.modalOverlay}>
              <View style={[localStyles.modalContainer, { backgroundColor: theme.card }]}> 
                <Text style={[localStyles.modalTitle, { color: theme.text }]}>Generate Item Report</Text>
                <Text style={[localStyles.modalSubtitle, { color: theme.secondaryText }]}>Choose report type:</Text>
                {/* Options */}
                <TouchableOpacity
                  style={[localStyles.option, reportType === 'all' && localStyles.optionSelected, { backgroundColor: reportType==='all'?theme.accent:theme.inputBackground, borderColor: theme.border }]}
                  onPress={() => setReportType('all')}
                  disabled={loading}
                >
                  <Ionicons name="document-text-outline" size={24} color={reportType === 'all' ? theme.buttonText : theme.text} />
                  <Text style={[localStyles.optionText, { color: reportType === 'all' ? theme.buttonText : theme.text }]}>All Items</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[localStyles.option, reportType === 'selected' && localStyles.optionSelected, { backgroundColor: reportType==='selected'?theme.accent:theme.inputBackground, borderColor: theme.border }]}
                  onPress={() => setReportType('selected')}
                  disabled={loading}
                >
                  <Ionicons name="list-outline" size={24} color={reportType === 'selected' ? theme.buttonText : theme.text} />
                  <Text style={[localStyles.optionText, { color: reportType === 'selected' ? theme.buttonText : theme.text }]}>Select Items</Text>
                </TouchableOpacity>

                {message ? <Text style={[localStyles.message, { color: theme.text }]}>{message}</Text> : null}
                {loading && <ActivityIndicator style={{ marginTop: 12 }} color={theme.text} />}

                {(!loading && message) ? (
                  <ThemedButton
                    title="OK"
                    onPress={() => {
                      setShowModal(false);
                      setMessage('');
                    }}
                    style={{ marginTop: 24, alignSelf: 'center', minWidth: 140 }}
                  />
                ) : (
                  <View style={{ flexDirection: 'row', gap: 12, marginTop: 24, width: '100%' }}>
                    <ThemedButton
                      title="Cancel"
                      variant="secondary"
                      onPress={() => {
                        if (!loading) {
                          setShowModal(false);
                          setMessage('');
                        }
                      }}
                      style={{ flex: 1 }}
                      disabled={loading}
                    />
                    <ThemedButton
                      title={loading ? 'Generating…' : 'Generate'}
                      onPress={handleGenerate}
                      style={{ flex: 1 }}
                      disabled={loading}
                    />
                  </View>
                )}
              </View>
            </View>
          </Modal>
        </>
      )}
    </View>
  );
}

const localStyles = StyleSheet.create({
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
  },
  detailsValue: {
    fontSize: 16,
    marginBottom: 12,
  },
  actionButton: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: 200,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  /* Modal */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    padding: 24,
    borderRadius: 12,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 15,
    marginBottom: 20,
    textAlign: 'center',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  optionSelected: {
    borderWidth: 0,
  },
  optionText: {
    fontSize: 16,
    fontWeight: '600',
  },
  message: {
    fontSize: 14,
    marginTop: 12,
    textAlign: 'center',
  },
  modalButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
});
